import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success('Welcome back!');
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F8FB] via-[#E8F2FF] to-[#F0E8FF] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/logo.png" alt="SkillRise Logo" className="w-14 h-14 rounded-2xl object-cover shadow-lg" />
            <div>
              <h1 className="text-4xl font-black tracking-tight text-[#1E293B]">SkillRise</h1>
              <p className="text-[10px] font-semibold text-[#64748B] tracking-widest uppercase">by Ekamraj Productions</p>
            </div>
          </div>
          <p className="text-lg text-[#64748B]">Welcome back! Let's continue your growth journey.</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl" data-testid="login-form">
          <h2 className="text-3xl font-bold text-[#1E293B] mb-6">Login</h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-[#1E293B] mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-[#4D96FF] focus:ring-4 focus:ring-[#4D96FF]/10 transition-all"
                  placeholder="your@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1E293B] mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-[#4D96FF] focus:ring-4 focus:ring-[#4D96FF]/10 transition-all"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#4D96FF] hover:bg-[#3B7BE0] text-white rounded-2xl font-bold text-lg border-b-4 border-[#3B7BE0] active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#64748B]">
              Don't have an account?{' '}
              <Link data-testid="login-to-signup-link" to="/signup" className="text-[#4D96FF] font-bold hover:underline">
                Sign up
              </Link>
            </p>
            <p className="text-[#64748B] text-sm mt-2">
              Teacher or Parent?{' '}
              <Link data-testid="login-to-school-signup-link" to="/school-signup" className="text-indigo-600 font-bold hover:underline">
                Join as School User
              </Link>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginPage;